import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import AccountSidebar from '../../components/AccountSidebar';

const SharedZerodhaAdminPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const location = useLocation();
  const navigate = useNavigate();

  const [sharedStatus, setSharedStatus] = useState(null);
  const [freeAccess, setFreeAccess] = useState(null);
  const [sharedApiKey, setSharedApiKey] = useState('');
  const [sharedApiSecret, setSharedApiSecret] = useState('');
  const [requestToken, setRequestToken] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [freeAccessForm, setFreeAccessForm] = useState({
    enabled: false,
    start_ist: '',
    end_ist: '',
  });

  const loadSharedStatus = async () => {
    if (!isAdmin) return;
    try {
      const resp = await api.get('/api/v1/admin/settings/shared-zerodha/status');
      setSharedStatus(resp.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to fetch shared Zerodha status';
      setMessage({ type: 'error', text: msg });
    }
  };

  const loadFreeAccess = async () => {
    if (!isAdmin) return;
    try {
      const resp = await api.get('/api/v1/admin/settings/nifty-free-access');
      setFreeAccess(resp.data);
      setFreeAccessForm({
        enabled: !!resp.data?.enabled,
        start_ist: resp.data?.start_ist || '',
        end_ist: resp.data?.end_ist || '',
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to fetch free access settings';
      setMessage({ type: 'error', text: msg });
    }
  };

  useEffect(() => {
    loadSharedStatus();
    loadFreeAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sharedAuth = params.get('shared_auth');
    const callbackMessage = params.get('message');
    if (!sharedAuth) return;

    const type = sharedAuth === 'success' ? 'success' : 'error';
    setMessage({
      type,
      text: callbackMessage || (type === 'success' ? 'Shared token rotated successfully.' : 'Shared token rotation failed.'),
    });
    loadSharedStatus();
    loadFreeAccess();
    navigate('/admin/shared-zerodha', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate]);

  const saveSharedCredentials = async () => {
    if (!sharedApiKey.trim() || !sharedApiSecret.trim()) {
      setMessage({ type: 'error', text: 'API Key and API Secret are required.' });
      return;
    }
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const resp = await api.put('/api/v1/admin/settings/shared-zerodha/credentials', {
        api_key: sharedApiKey.trim(),
        api_secret: sharedApiSecret.trim(),
      });
      setSharedStatus(resp.data);
      setSharedApiSecret('');
      setMessage({ type: 'success', text: 'Shared Zerodha credentials saved successfully.' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to save shared credentials';
      setMessage({ type: 'error', text: msg });
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedLoginUrl = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const resp = await api.get('/api/v1/admin/settings/shared-zerodha/login-url');
      setLoginUrl(resp.data.login_url);
      setMessage({ type: 'success', text: 'Login URL generated. Open it to get request_token.' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to generate login URL';
      setMessage({ type: 'error', text: msg });
    } finally {
      setLoading(false);
    }
  };

  const startSharedLoginAndRotate = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const resp = await api.get('/api/v1/admin/settings/shared-zerodha/login-url');
      const url = resp.data.login_url;
      setLoginUrl(url);
      sessionStorage.setItem('zerodha_auth_flow', 'shared_zerodha_admin');
      window.location.href = url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to start shared Zerodha login';
      setMessage({ type: 'error', text: msg });
      setLoading(false);
    }
  };

  const rotateSharedToken = async () => {
    if (!requestToken.trim()) {
      setMessage({ type: 'error', text: 'request_token is required to rotate token.' });
      return;
    }
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const resp = await api.post('/api/v1/admin/settings/shared-zerodha/rotate-token', {
        request_token: requestToken.trim(),
      });
      setSharedStatus(resp.data);
      setRequestToken('');
      setMessage({ type: 'success', text: 'Shared Zerodha access token rotated successfully.' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to rotate shared token';
      setMessage({ type: 'error', text: msg });
    } finally {
      setLoading(false);
    }
  };

  const saveFreeAccessSettings = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        enabled: !!freeAccessForm.enabled,
        start_ist: freeAccessForm.start_ist || null,
        end_ist: freeAccessForm.end_ist || null,
      };
      const resp = await api.put('/api/v1/admin/settings/nifty-free-access', payload);
      setFreeAccess(resp.data);
      setMessage({ type: 'success', text: 'Free NIFTY access settings updated.' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to update free access settings';
      setMessage({ type: 'error', text: msg });
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: 'url(/assets/profilepage/profile_page_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <Navbar />

      <div className="flex-1 flex items-center justify-center py-16 lg:py-12">
        <div className="max-w-5xl w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-4">
              <AccountSidebar activePage="admin-shared-zerodha" />
              <div className="md:col-span-3 bg-white rounded-r-2xl p-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Shared Zerodha Admin</h1>
                <p className="text-sm text-gray-600 mb-6">
                  Manage shared Zerodha credentials and token rotation for free NIFTY mode.
                </p>

                {message.text && (
                  <div
                    className={`p-3 rounded-md mb-4 text-sm ${
                      message.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}
                  >
                    {message.text}
                  </div>
                )}

                <div className="border border-emerald-200 rounded-lg p-6 bg-emerald-50/40 mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Free NIFTY Access Window</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Enable free NIFTY chart/signal access for logged-in users during a configured IST window.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                    <div><span className="font-medium">Enabled:</span> {freeAccess?.enabled ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Active Now:</span> {freeAccess?.active_now ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Start IST:</span> {freeAccess?.start_ist || 'N/A'}</div>
                    <div><span className="font-medium">End IST:</span> {freeAccess?.end_ist || 'N/A'}</div>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <input
                      id="free-nifty-enabled"
                      type="checkbox"
                      checked={!!freeAccessForm.enabled}
                      onChange={(e) =>
                        setFreeAccessForm((prev) => ({ ...prev, enabled: e.target.checked }))
                      }
                    />
                    <label htmlFor="free-nifty-enabled" className="text-sm text-gray-800">
                      Enable Free NIFTY Access
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input
                      type="datetime-local"
                      value={freeAccessForm.start_ist}
                      onChange={(e) =>
                        setFreeAccessForm((prev) => ({ ...prev, start_ist: e.target.value }))
                      }
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="datetime-local"
                      value={freeAccessForm.end_ist}
                      onChange={(e) =>
                        setFreeAccessForm((prev) => ({ ...prev, end_ist: e.target.value }))
                      }
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={saveFreeAccessSettings}
                      disabled={loading}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
                    >
                      Save Free Access Settings
                    </button>
                    <button
                      onClick={loadFreeAccess}
                      disabled={loading}
                      className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-60"
                    >
                      Refresh Free Access
                    </button>
                  </div>
                </div>

                <div className="border border-indigo-200 rounded-lg p-6 bg-indigo-50/40">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                    <div><span className="font-medium">Configured:</span> {sharedStatus?.configured ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Has API Key:</span> {sharedStatus?.has_api_key ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Has API Secret:</span> {sharedStatus?.has_api_secret ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Has Access Token:</span> {sharedStatus?.has_access_token ? 'Yes' : 'No'}</div>
                    <div className="md:col-span-2">
                      <span className="font-medium">Token Updated At:</span> {sharedStatus?.token_updated_at || 'N/A'}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      placeholder="Shared Zerodha API Key"
                      value={sharedApiKey}
                      onChange={(e) => setSharedApiKey(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="password"
                      placeholder="Shared Zerodha API Secret"
                      value={sharedApiSecret}
                      onChange={(e) => setSharedApiSecret(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      onClick={saveSharedCredentials}
                      disabled={loading}
                      className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60"
                    >
                      Save Shared Credentials
                    </button>
                    <button
                      onClick={startSharedLoginAndRotate}
                      disabled={loading}
                      className="px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-60"
                    >
                      Login & Auto Rotate
                    </button>
                    <button
                      onClick={fetchSharedLoginUrl}
                      disabled={loading}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
                    >
                      Generate Login URL
                    </button>
                    <button
                      onClick={loadSharedStatus}
                      disabled={loading}
                      className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-60"
                    >
                      Refresh Status
                    </button>
                  </div>

                  {loginUrl && (
                    <div className="mb-4 text-sm">
                      <p className="font-medium text-gray-800 mb-1">Shared Zerodha Login URL:</p>
                      <a
                        href={loginUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-700 underline break-all"
                      >
                        {loginUrl}
                      </a>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center">
                    <input
                      type="text"
                      placeholder="Paste request_token here"
                      value={requestToken}
                      onChange={(e) => setRequestToken(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                      onClick={rotateSharedToken}
                      disabled={loading}
                      className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-60"
                    >
                      Rotate Shared Token
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SharedZerodhaAdminPage;
